/**
 * The quick-note panel's headless rules (feedback quick capture spec 01 §§2-3): what an image may be, what is sent
 * with a note, the one-line summary of what was captured, where "Sent" links to, and what a failed send says.
 * Pure: no DOM, no React.
 */
import type { ApiFailure } from "../data/api/recent-errors";
import { ApiError } from "../data/api/errors";
import type * as API from "../data/api-types";
import { extractFieldErrors } from "../data/validation";
import { paths } from "../routing/routes";

/** The image caps the backend enforces (`feedback_module` upload_feedback_image): 10 MB each, 10 per note. */
export const MAX_QUICK_NOTE_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_QUICK_NOTE_IMAGES = 10;
export const QUICK_NOTE_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;

/** The qualified id of the Feedback object. */
export const FEEDBACK_OBJECT_ID = "feedback.feedback_item";

/** Why an image cannot go on the note, or null when it can. `imagesOnNote` counts those already added. */
export function imageRefusal(
  image: { type: string; size: number },
  imagesOnNote: number,
): string | null {
  if (imagesOnNote >= MAX_QUICK_NOTE_IMAGES) {
    return `A note takes at most ${MAX_QUICK_NOTE_IMAGES} images.`;
  }
  if (!(QUICK_NOTE_IMAGE_TYPES as readonly string[]).includes(image.type)) {
    return "Only PNG, JPEG or WebP images can go on a note.";
  }
  if (image.size > MAX_QUICK_NOTE_IMAGE_BYTES) {
    return "An image can be at most 10 MB.";
  }
  return null;
}

/** What the panel knew about the page when it opened. */
export interface QuickNoteCapture {
  environment: string;
  /** The build's commit or release id; "dev" when the build has none. */
  buildSha: string;
  previewId: string | null;
  /** The path of the page, without its query string. */
  pagePath: string;
  viewport: string;
}

/** The One object and record a page path shows, or nulls on any other page. */
export function objectOnPage(pagePath: string): {
  objectId: string | null;
  recordKey: string | null;
} {
  const match = /^\/objects\/([^/]+)(?:\/([^/]+))?\/?$/.exec(pagePath);
  if (!match) return { objectId: null, recordKey: null };
  return {
    objectId: decodeURIComponent(match[1]!),
    recordKey: match[2] ? decodeURIComponent(match[2]) : null,
  };
}

/**
 * The one line under the screenshot: where the note was raised, e.g. "Orders · ORD-K48193 · staging". `objectLabel`
 * is the page's object in words when the page is an object page; any other page is named by its path.
 */
export function capturedSummaryLine(
  capture: QuickNoteCapture,
  objectLabel: string | null,
): string {
  const { objectId, recordKey } = objectOnPage(capture.pagePath);
  const where =
    objectId !== null ? (objectLabel ?? objectId) : capture.pagePath;
  const environment = capture.previewId
    ? `${capture.environment} preview`
    : capture.environment;
  return [where, recordKey, environment].filter(Boolean).join(" · ");
}

/** A console error as the panel keeps it; the message has already been made safe by `safeConsoleMessage`. */
export interface ConsoleFailure {
  message: string;
}

const MAX_CONSOLE_MESSAGE = 200;

/**
 * A console error's text as it may be sent: without any URL's query string or fragment (they can carry search text),
 * on one line, and cut short. A console message is free text, so it is kept to the first line only.
 */
export function safeConsoleMessage(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const withoutQueries = firstLine.replace(
    /([^\s"'<>?#]+)[?#][^\s"'<>]*/g,
    "$1",
  );
  return withoutQueries.length > MAX_CONSOLE_MESSAGE
    ? `${withoutQueries.slice(0, MAX_CONSOLE_MESSAGE - 1)}…`
    : withoutQueries;
}

/** The failures the note reports, in the shape the API takes: failed calls first, then console errors. */
export function recentErrorsOf(
  apiFailures: readonly ApiFailure[],
  consoleFailures: readonly ConsoleFailure[],
): API.FeedbackQuickNoteRecentError[] {
  return [
    ...apiFailures.map((failure): API.FeedbackQuickNoteRecentError => ({
      kind: "api",
      method: failure.method,
      path: failure.path,
      http_status: failure.status,
      trace_id: failure.traceId,
    })),
    ...consoleFailures.map((failure): API.FeedbackQuickNoteRecentError => ({
      kind: "console",
      message: failure.message,
    })),
  ];
}

/** The body of Send quick note for one note. `requestId` is fixed per attempt, so a resend cannot double the note. */
export function quickNoteRequest(input: {
  note: string;
  imageIds: readonly string[];
  capture: QuickNoteCapture;
  recentErrors: API.FeedbackQuickNoteRecentError[];
  requestId: string;
}): API.SendQuickNoteRequest {
  const { objectId, recordKey } = objectOnPage(input.capture.pagePath);
  return {
    note: input.note.trim(),
    image_ids: [...input.imageIds],
    environment: input.capture.environment,
    build_sha: input.capture.buildSha,
    preview_id: input.capture.previewId,
    page_path: input.capture.pagePath,
    object_id: objectId,
    record_key: recordKey,
    viewport: input.capture.viewport,
    recent_errors: input.recentErrors,
    request_id: input.requestId,
  };
}

/** Where the "Sent" line leads: the note's record in the hub's One (a new tab) or in this One. */
export function sentNoteLink(
  created: API.CommandResult["created"],
  hubApiBaseUrl: string,
): { href: string; isExternal: boolean } | null {
  if (!created) return null;
  const path = paths.objectRecord({
    objectId: created.object,
    recordKey: created.id,
  });
  const hubOrigin = originOf(hubApiBaseUrl);
  return hubOrigin
    ? { href: `${hubOrigin}${path}`, isExternal: true }
    : { href: path, isExternal: false };
}

/** The hub's Feedback list (new tab), or null when this environment is the hub. */
export function hubFeedbackListHref(
  hubApiBaseUrl: string,
  search?: string,
): string | null {
  const hubOrigin = originOf(hubApiBaseUrl);
  return hubOrigin
    ? `${hubOrigin}${paths.objectList({ objectId: FEEDBACK_OBJECT_ID, ...(search ? { search } : {}) })}`
    : null;
}

/** The frontend of the hub lives on the origin of its API (`https://host/api` -> `https://host`). */
function originOf(apiBaseUrl: string): string | null {
  if (!apiBaseUrl.trim()) return null;
  try {
    return new URL(apiBaseUrl).origin;
  } catch {
    return null;
  }
}

/** What a failed send tells the person, so they know whether to try again. The text and images are kept. */
export function describeSendFailure(err: unknown): string {
  if (err instanceof ApiError) {
    const fieldErrors = extractFieldErrors(err);
    if (fieldErrors.length > 0) return fieldErrors[0]!.message;
    if (err.status === 401 || err.status === 403) {
      return "The feedback hub did not accept your sign-in. Send again to sign in once more.";
    }
    // A refusal (409) will not pass on a resend, so its own reason says what to do instead.
    if (err.status === 409 && err.detail) return err.detail;
    return `The note was not sent (error ${err.status}). Send again in a moment.`;
  }
  if (err instanceof TypeError) {
    return "Couldn't reach the feedback hub. Check your connection and send again.";
  }
  if (err instanceof Error && err.message) return err.message;
  return "The note was not sent. Send again in a moment.";
}
